import React, { useEffect } from 'react';
import FilterRow from '@/components/Filter/FilterRow/FilterRow.tsx';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition.ts';
import { UserFilter } from '@/API.ts';
import SaveFilterDialog from '@/components/Filter/SaveFilterDialog/SaveFilterDialog.tsx';
import { FilterRowSelection } from '@lambda-layer/types/filter';
import { CountMapUtils } from '@/utils/CountMapUtils';
import { ControlType } from '@lambda-layer/models/FilterDefinition/FilterDefinitionFields.ts';

interface FilterListProps {
  filterDefinitions: FilterDefinition[];
  filterSelections: Record<string, FilterRowSelection>;
  filterCounts: Record<string, Map<string | null, number>>;
  loading: Record<string, boolean>;
  updateSelection: (
    index: string,
    filterRowSelection: FilterRowSelection
  ) => void;
  onFocusChange: (index: string) => void;
  onChange: (fieldId: string, filterRowSelection: FilterRowSelection) => void;
  calculateFilterCounts: () => Record<string, number>;
  onFilterSave?: (
    filterName: string,
    filterDefinitions: FilterDefinition[],
    filterSelections: Record<string, FilterRowSelection>
  ) => Promise<UserFilter>;
  onFilterSelectionCountChange?: (count: number) => void;
  /** When false, hides SaveFilterDialog and star button in rows (default: true). */
  allowSaveFilters?: boolean;
}

/**
 * Sorts a map by its keys.
 * @param filterDefintion The filter field definition
 * @param map The map to sort.
 * @param dataType The type of the data in the map.
 * @returns A new map with the keys sorted.
 * @example sortMap(new Map([['b', 2], ['a', 1]])) // Returns new Map([['a', 1], ['b', 2]])
 * @example sortMap(new Map([['2', 2], ['1', 1]]), 'number') // Returns new Map([['1', 1], ['2', 2]])
 */
const sortMap = (
  filterDefintion: FilterDefinition,
  map: Map<string | null, number>,
  dataType?: string
): Map<string | null, number> => {
  if (filterDefintion.valueSortFn) {
    const sortedEntries = Array.from(map.entries());
    const sortedKeys = sortedEntries.map(([key]) => key);

    // Use custom sorting function
    const customSortedKeys = filterDefintion.valueSortFn(sortedKeys);

    // Rebuild map based on custom sorted keys
    const customSortedMap = new Map<string, number>();
    for (const key of customSortedKeys) {
      if (map.has(key)) {
        customSortedMap.set(key, map.get(key) || 0);
      } else {
        // If the map doesn't have the key, still set count 0 for completeness
        customSortedMap.set(key, 0);
      }
    }
    return customSortedMap;
  }
  const sortedEntries =
    dataType === 'number'
      ? [...map.entries()].sort((a, b) => Number(a[0]) - Number(b[0]))
      : [...map.entries()].sort();
  return new Map(sortedEntries);
};

const arraysAreEqual = (array1: unknown[], array2: unknown[]) => {
  return (
    array1.length === array2.length &&
    array1.every((value, index) => value === array2[index])
  );
};

const getLastSelectionId = (
  filterDefinitions: FilterDefinition[],
  filterSelections: Record<string, FilterRowSelection>
) => {
  const match = Object.entries(filterSelections)
    .reverse()
    .find(([id, filterRowSelection]) => {
      if (filterRowSelection.value.length > 0) {
        const filterDefinition = filterDefinitions.find(fd => fd.id === id);
        if (!filterDefinition) {
          return false;
        }
        // Exclude fields without a control
        if (!filterDefinition.controlType) {
          return false;
        }

        // For boolean controls, check both value and relational operator
        if (filterDefinition.controlType === ControlType.BOOLEAN_TOGGLE) {
          // If there's no default value or operator, consider it changed
          if (
            !filterDefinition.defaultValue ||
            !filterDefinition.defaultRelationalOperator
          ) {
            return true;
          }

          // Check if both value and operator match defaults
          const valueMatches = arraysAreEqual(
            filterDefinition.defaultValue,
            filterRowSelection.value
          );
          const operatorMatches =
            filterDefinition.defaultRelationalOperator ===
            filterRowSelection.relationalOperator;

          // Consider it unchanged only if both value and operator match defaults
          return !(valueMatches && operatorMatches);
        }

        // For non-boolean controls, use the original logic
        return !(
          filterDefinition.defaultValue &&
          arraysAreEqual(
            filterDefinition.defaultValue,
            filterRowSelection.value
          )
        );
      }
      return false;
    });
  return match ? match[0] : null;
};

const FilterList: React.FC<FilterListProps> = ({
  filterDefinitions,
  filterSelections,
  filterCounts,
  loading,
  updateSelection,
  onFocusChange,
  onChange,
  calculateFilterCounts,
  onFilterSave,
  onFilterSelectionCountChange,
  allowSaveFilters = true,
}) => {
  const [saveFilterDialogOpen, setSaveFilterDialogOpen] = React.useState(false);
  const [filterSelectionCount, setFilterSelectionCount] = React.useState(0);

  const handleSaveFilterClick = () => {
    setSaveFilterDialogOpen(true);
  };

  const handleFilterSave = (filterName: string) => {
    if (onFilterSave) {
      void onFilterSave(filterName, filterDefinitions, filterSelections);
    }
  };

  useEffect(() => {
    const lastSelectionId = getLastSelectionId(
      filterDefinitions,
      filterSelections
    );

    // Compute all counts by calling the function
    const counts = calculateFilterCounts();
    const relevantFilter = filterDefinitions.find(
      ({ id, isDatabaseField }) => id === lastSelectionId && isDatabaseField
    );

    if (relevantFilter) {
      const { id } = relevantFilter;
      const currentCount = counts[id] ?? 0;

      if (
        onFilterSelectionCountChange &&
        filterSelectionCount !== currentCount
      ) {
        setFilterSelectionCount(currentCount);
        onFilterSelectionCountChange(currentCount);
      }
    }
  }, [
    filterDefinitions,
    filterSelections,
    calculateFilterCounts,
    filterSelectionCount,
    setFilterSelectionCount,
    onFilterSelectionCountChange,
  ]);

  const counts = calculateFilterCounts();

  return (
    <>
      {allowSaveFilters && (
        <SaveFilterDialog
          open={saveFilterDialogOpen}
          onClose={() => setSaveFilterDialogOpen(false)}
          onSave={handleFilterSave}
        />
      )}
      {filterDefinitions.map(filterDefinition => {
        const selection = filterSelections[filterDefinition.id];
        const fieldSelectionHash = selection.externalUpdateHash
          ? selection.externalUpdateHash
          : filterDefinition.id;
        const dataType = filterDefinition.databaseMetadata?.dataType;

        const countsMap =
          filterCounts[filterDefinition.id] || new Map<string, number>();

        // Keep preselected values visible, but never inject the ALL sentinel.
        // Also preserve real nulls (avoid String(null) => "null").
        const selectionKeys: (string | null)[] = selection.value
          .map(v => (v == null ? null : String(v)))
          .filter(v => v !== 'ALL');

        const allKeys = new Set<string | null>([
          ...countsMap.keys(),
          ...selectionKeys,
        ]);

        const fullMap = new Map<string | null, number>(
          Array.from(allKeys).map(key => [key, countsMap.get(key) || 0])
        );

        const firstSelectableFilterRow = filterDefinitions.find(
          f => f.controlType && f.csvMetadata.columnOrder && !f.isAdvancedFilter
        );

        return (
          <FilterRow
            autoFocus={filterDefinition.id === firstSelectableFilterRow?.id}
            key={fieldSelectionHash} // Re-render on selection change
            filterDefinition={filterDefinition}
            isLoading={loading[filterDefinition.id]}
            filterRowSelection={selection}
            onChange={(selection: FilterRowSelection) => {
              updateSelection(filterDefinition.id, selection);
              onChange(filterDefinition.id, selection);
            }}
            onFocusChange={() => onFocusChange(filterDefinition.id)}
            // Exclude the 'count' entry from dropdown options; total is shown via recordCount prop
            options={CountMapUtils.filterOutTotalCount(
              Array.from(sortMap(filterDefinition, fullMap, dataType).entries())
            ).map(([value, count]) => ({
              label: value,
              value,
              count,
            }))}
            recordCount={
              filterDefinition.isDatabaseField
                ? counts[filterDefinition.id]
                : undefined
            }
            displaySaveFilters={
              allowSaveFilters &&
              filterDefinition.id ===
                getLastSelectionId(filterDefinitions, filterSelections)
            }
            onSaveFilterClick={
              allowSaveFilters ? handleSaveFilterClick : undefined
            }
          />
        );
      })}
    </>
  );
};

export default FilterList;
